import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, ChevronDown, Settings, Sparkles, Zap, LayoutGrid } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { handleError } from "@/lib/errorHandler";
import { DashboardNav } from "@/components/layout/DashboardNav";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ProactiveInsights } from "@/components/agent/ProactiveInsights";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

interface Workspace {
  id: string;
  name: string;
  created_at: string;
}

interface Base {
  id: string;
  name: string;
  icon: string;
  description: string | null;
  created_at: string;
  workspace_id: string;
}

const QUICK_START_TEMPLATES = [
  { name: "Project Tracker", icon: "📋", description: "Tasks, deadlines, team assignments" },
  { name: "CRM", icon: "👥", description: "Contacts, deals, pipeline" },
  { name: "Inventory", icon: "📦", description: "Products, stock, orders" },
  { name: "Content Calendar", icon: "📅", description: "Posts, channels, schedule" },
];

const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [bases, setBases] = useState<Base[]>([]);
  const [activeWorkspace, setActiveWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [baseName, setBaseName] = useState("");
  const [baseIcon, setBaseIcon] = useState("📊");
  const [creating, setCreating] = useState(false);
  const [workspaceDialogOpen, setWorkspaceDialogOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      initializeDashboard();
    }
  }, [user]);

  const initializeDashboard = async () => {
    try {
      const { data: workspacesData, error: wsError } = await supabase
        .from("workspaces")
        .select("*")
        .order("created_at", { ascending: true });

      if (wsError) throw wsError;

      let workspacesList = workspacesData || [];

      if (workspacesList.length === 0) {
        const { data: newWorkspace, error: createError } = await supabase
          .from("workspaces")
          .insert([{ name: "My Workspace", owner_id: user!.id }])
          .select()
          .single();

        if (createError) throw createError;

        await supabase
          .from("workspace_members")
          .insert([{ workspace_id: newWorkspace.id, user_id: user!.id, role: "owner" }]);

        workspacesList = [newWorkspace];
      }

      setWorkspaces(workspacesList);
      const active = workspacesList[0];
      setActiveWorkspace(active);
      
      if (active) {
        await fetchBases(active.id);
      }
    } catch (error: any) {
      handleError(error, "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchBases = async (workspaceId: string) => {
    try {
      const { data, error } = await supabase
        .from("bases")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setBases(data || []);
    } catch (error: any) {
      handleError(error, "Failed to load bases");
    }
  };

  const switchWorkspace = async (workspace: Workspace) => {
    setActiveWorkspace(workspace);
    setLoading(true);
    await fetchBases(workspace.id);
    setLoading(false);
  };

  const createBase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeWorkspace || !user?.id) return;

    setCreating(true);

    try {
      const { data: base, error } = await supabase
        .from("bases")
        .insert([{
          workspace_id: activeWorkspace.id,
          name: baseName,
          icon: baseIcon,
          created_by: user.id
        }])
        .select()
        .single();

      if (error) throw error;

      toast.success("Base created!");
      setBaseName("");
      setBaseIcon("📊");
      setCreateDialogOpen(false);
      navigate(`/base/${base.id}`);
    } catch (error: any) {
      handleError(error, "Failed to create base");
    } finally {
      setCreating(false);
    }
  };

  const createFromTemplate = async (template: typeof QUICK_START_TEMPLATES[0]) => {
    if (!activeWorkspace || !user?.id) return;

    setCreating(true);

    try {
      const { data: base, error } = await supabase
        .from("bases")
        .insert([{
          workspace_id: activeWorkspace.id,
          name: template.name,
          icon: template.icon,
          description: template.description,
          created_by: user.id
        }])
        .select()
        .single();

      if (error) throw error;

      toast.success(`${template.name} created!`);
      navigate(`/base/${base.id}`);
    } catch (error: any) {
      handleError(error, "Failed to create base");
    } finally {
      setCreating(false);
    }
  };

  const createWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    try {
      const { data: workspace, error } = await supabase
        .from("workspaces")
        .insert([{ name: newWorkspaceName, owner_id: user.id }])
        .select()
        .single();

      if (error) throw error;

      await supabase
        .from("workspace_members")
        .insert([{ workspace_id: workspace.id, user_id: user.id, role: "owner" }]);

      toast.success("Workspace created!");
      setNewWorkspaceName("");
      setWorkspaceDialogOpen(false);
      setWorkspaces([...workspaces, workspace]);
      switchWorkspace(workspace);
    } catch (error: any) {
      handleError(error, "Failed to create workspace");
    }
  };

  if (authLoading || loading) {
    return <LoadingSpinner fullScreen text="Loading your workspace..." />;
  }

  return (
    <div className="min-h-screen bg-background pb-mobile-nav">
      <DashboardNav>
        {/* Workspace Switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2 font-medium">
              <span className="truncate max-w-[150px]">{activeWorkspace?.name}</span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {workspaces.map((ws) => (
              <DropdownMenuItem
                key={ws.id}
                onClick={() => switchWorkspace(ws)}
                className={ws.id === activeWorkspace?.id ? "bg-accent" : ""}
              >
                {ws.name}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setWorkspaceDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Workspace
            </DropdownMenuItem>
            {activeWorkspace && (
              <DropdownMenuItem onClick={() => navigate(`/workspace/${activeWorkspace.id}/settings`)}>
                <Settings className="h-4 w-4 mr-2" />
                Workspace Settings
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </DashboardNav>

      <main className="container mx-auto px-4 py-6 md:py-10">
        {/* AI Insights */}
        <div className="mb-8">
          <ProactiveInsights />
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Your Bases</h1>
            <p className="text-muted-foreground mt-1">Build and manage your data</p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} className="gap-2 hidden sm:inline-flex">
            <Plus className="h-4 w-4" />
            New Base
          </Button>
        </div>

        {/* Content */}
        {bases.length === 0 ? (
          <div className="space-y-10 animate-fade-in">
            {/* Empty State */}
            <Card className="border-dashed border-2 border-border/50">
              <CardContent className="flex flex-col items-center justify-center py-16 px-4">
                <div className="h-16 w-16 rounded-2xl gradient-primary flex items-center justify-center mb-6 shadow-lg">
                  <Sparkles className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Create your first base</h3>
                <p className="text-muted-foreground text-center max-w-md mb-6">
                  Start from scratch or use a template to get going quickly
                </p>
                <Button onClick={() => setCreateDialogOpen(true)} size="lg" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create Base
                </Button>
              </CardContent>
            </Card>

            {/* Quick Start Templates */}
            <div>
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                Quick Start
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {QUICK_START_TEMPLATES.map((template) => (
                  <Card
                    key={template.name}
                    className="hover-lift cursor-pointer group border-border/50"
                    onClick={() => createFromTemplate(template)}
                  >
                    <CardHeader className="p-4 pb-3">
                      <CardTitle className="flex items-center gap-3 text-base">
                        <span className="text-2xl">{template.icon}</span>
                        <span className="group-hover:text-primary transition-colors">{template.name}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <CardDescription className="text-xs line-clamp-2">
                        {template.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Button 
                variant="link" 
                className="mt-4 p-0 h-auto text-primary"
                onClick={() => navigate("/templates")}
              >
                Browse all templates →
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 animate-fade-in">
            {bases.map((base) => (
              <Card
                key={base.id}
                className="hover-lift cursor-pointer group border-border/50"
                onClick={() => navigate(`/base/${base.id}`)}
              >
                <CardHeader className="p-4 md:p-5">
                  <CardTitle className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center text-xl group-hover:bg-primary/20 transition-colors">
                      {base.icon}
                    </div>
                    <span className="truncate group-hover:text-primary transition-colors text-base md:text-lg">
                      {base.name}
                    </span>
                  </CardTitle>
                  {base.description && (
                    <CardDescription className="line-clamp-2 text-xs mt-2">
                      {base.description}
                    </CardDescription>
                  )}
                </CardHeader>
              </Card>
            ))}
            {/* Add New Base Card */}
            <Card
              className="border-dashed border-2 border-border/50 hover:border-primary/50 transition-all cursor-pointer flex items-center justify-center min-h-[120px]"
              onClick={() => setCreateDialogOpen(true)}
            >
              <CardContent className="flex flex-col items-center justify-center p-4 text-muted-foreground hover:text-primary transition-colors">
                <Plus className="h-8 w-8 mb-2" />
                <span className="text-sm font-medium">New Base</span>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Create Base Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Base</DialogTitle>
            <DialogDescription>
              Give your base a name and icon to get started
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createBase} className="space-y-4">
            <div className="flex gap-3">
              <div className="space-y-2">
                <Label>Icon</Label>
                <Input
                  value={baseIcon}
                  onChange={(e) => setBaseIcon(e.target.value)}
                  maxLength={2}
                  className="w-16 text-center text-xl"
                />
              </div>
              <div className="flex-1 space-y-2">
                <Label>Name</Label>
                <Input
                  placeholder="My Base"
                  value={baseName}
                  onChange={(e) => setBaseName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={creating}>
              {creating ? "Creating..." : "Create Base"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Workspace Dialog */}
      <Dialog open={workspaceDialogOpen} onOpenChange={setWorkspaceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Workspace</DialogTitle>
            <DialogDescription>
              Workspaces help you organize bases and collaborate with teams
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createWorkspace} className="space-y-4">
            <div className="space-y-2">
              <Label>Workspace Name</Label>
              <Input
                placeholder="Marketing Team"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full">
              Create Workspace
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <MobileBottomNav showCreateButton onCreateClick={() => setCreateDialogOpen(true)} />
    </div>
  );
};

export default Dashboard;